import { AppNavigation } from "@rubbl-core";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { forkJoin, of, Subscription } from "rxjs";
import { AuthenticationService, EnvironmentConfig, isStorefrontEnabled, liveAttachmentMachineTypes } from "../../../core/core";
import { User } from "oidc-client";
import { OverlayService } from "../../services/overlay.service";
import { trigger, transition, style, animate } from "@angular/animations";
import { RoutesService } from "../../services/routes.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AzureSearchRequest, AzureSearchService } from "../../../azure-search/azure-search";
import { ATTACHMENTS_PRIMARY_TYPE, ATTACHMENT_MACHINE_TYPES, InventoryItem } from "../../../inventory/inventory";
import { HEADER_LOCATIONS, headerLocationRoute, isCommerceNavigationUrl } from "../header-nav-data";
import { NavigationEnd, Router } from "@angular/router";
import { catchError, filter, map } from "rxjs/operators";

interface MobileShopSection { key: string; label: string; route?: string; queryParams?: Record<string, unknown>; filter?: string; }
interface MobileCategory { name: string; handle?: string; count: number; }

// Same inventory scope the desktop header dropdowns use for their popular categories.
const MARKETPLACE_BASE_FILTER =
  "(status eq 'Available' or status eq 'Onboarding') and (rentalRate ne 0) and (requiresAdminApproval eq false) and (approvalStatus eq 'Approved' or approvalStatus eq null)";

function flyInFromAbove(speed: string) {
  return trigger("flyInFromAbove", [transition(":enter", [style({ transform: "translateY(-100%)" }), animate(speed, style({ transform: "translateY(0)" }))])]);
}

declare let environment: EnvironmentConfig;

const connectLandingPages = {
  label: "Rubbl Connect",
  action: () => {
    // parent row; children hold the landing links
  },
  borderBottom: true,
  children: [
    {
      label: "Rubbl Connect",
      route: "/connect",
    },
    {
      label: "Earn with your machines",
      route: "/machines",
    },
    {
      label: "Haul equipment",
      route: "/hauling",
    },
    {
      label: "Service equipment",
      route: "/service",
    },
  ],
};

@Component({
  selector: "app-header-overlay",
  templateUrl: "./header-overlay.component.html",
  styleUrls: ["./header-overlay.component.scss"],
  animations: [flyInFromAbove("350ms")],
})
export class HeaderOverlayComponent implements OnInit, OnDestroy {
  user: User;
  showOverlay = false;
  overlaySubscription: Subscription;
  private authenticationSubscription: Subscription;
  private active = false;
  private authenticationEpoch = 0;

  menuItems: any[] = [];

  // Mobile replacement for the old chip row: each top-level tab expands inside this menu.
  readonly shopSections: MobileShopSection[] = [
    { key: "rentals", label: "Equipment Rentals", route: "equipment-rentals", queryParams: { radius: "anywhere" } },
    { key: "buy", label: "Buy it Now", route: "equipment-for-sale", queryParams: { buyItNowEnabled: true, radius: "anywhere" }, filter: " and (buyItNowEnabled eq true)" },
    { key: "rto", label: "Rent to Own", route: "equipment-rentals", queryParams: { rpoEnabled: true, radius: "anywhere" }, filter: " and (rpoEnabled eq true)" },
    { key: "attachments", label: "Attachments" },
    { key: "locations", label: "Locations" },
  ];
  readonly attachmentMachineTypes = liveAttachmentMachineTypes(ATTACHMENT_MACHINE_TYPES, environment);
  readonly locations = HEADER_LOCATIONS;
  openSection = "";
  openMachine = "";
  openState = "";
  popularCategories: Record<string, MobileCategory[]> = {};
  machineCategories: Record<string, MobileCategory[]> = {};
  private readonly categoriesEndpoint = (environment.commerceProductsApiDomain ?? environment.assetsApiDomain) + "/api/assets/v2/commerce-products/public/categories";

  /** Rental pages keep main's overlay; the expandable Shop menu only appears on Shop URLs. */
  commerceNavigation = false;
  readonly storefrontEnabled = isStorefrontEnabled(environment);
  private routerSubscription: Subscription;

  constructor(private overlayService: OverlayService, private authenticationService: AuthenticationService, public routesService: RoutesService,
    private azureSearchService: AzureSearchService, private http: HttpClient, private router: Router) {}

  ngOnInit() {
    this.active = true;
    this.commerceNavigation = isCommerceNavigationUrl(this.router.url);
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => (this.commerceNavigation = isCommerceNavigationUrl(event.urlAfterRedirects || event.url)));
    this.loadAttachmentMenus();
    this.overlayService.registerOverlay("header");
    this.overlaySubscription = this.overlayService.subscribeToOverlayUpdates("header", showOverlay => (this.showOverlay = showOverlay));
    this.authenticationSubscription = this.authenticationService.user$.subscribe(user => {
      this.authenticationEpoch++;
      this.applyUser(user);
    });
    const epoch = this.authenticationEpoch;
    this.authenticationService.getUser().then(user => {
      if (this.active && epoch === this.authenticationEpoch) this.applyUser(user);
    }).catch(() => {
      if (this.active && epoch === this.authenticationEpoch) this.applyUser(null);
    });
  }

  private applyUser(user: User) {
    this.user = user && !user.expired ? user : null;
    this.rebuildMenu();
  }

  private rebuildMenu() {
    this.menuItems = [];

    if (this.user) {
      this.menuItems = Object.assign([], this.routesService.allNavigationItems);
      this.menuItems.push(
        {
          label: "List a machine",
          route: "/owner/fleet/create",
        },
        {
          label: "Knowledge base",
          action: () => {
            AppNavigation.navigate("https://support.rubbl.com/");
          },
          borderBottom: true,
        },
        {
          label: "(866) 455-8531",
          action: () => {
            AppNavigation.navigate(`tel:(866) 455-8531`);
          },
        },
        {
          label: "Logout",
          borderBottom: true,
          action: () => this.authenticationService.logout(),
        },
      );
    }

    if (!this.user) {
      this.menuItems.push({
        label: "Learn more",
        action: () => {
          // do nothing
        },
        borderBottom: true,
        children: [
          {
            label: "How Rubbl works",
            route: "/lp/rent-machines",
          },
          {
            label: "Renter FAQs",
            action: () => {
              AppNavigation.navigate(`https://support.rubbl.com/renters`);
            },
          },
          {
            label: "Rental Terms",
            route: "/help/rental-terms",
          },
          {
            label: "Become an Owner",
            route: "/lp/list-your-machines",
          },
          {
            label: "Insurance & Protection",
            action: () => {
              AppNavigation.navigate(`https://support.rubbl.com/insurance-protection`);
            },
          },
          {
            label: "Earnings Calculator",
            route: "/earnings-calculator",
          },
          {
            label: "Owner FAQs",
            action: () => {
              AppNavigation.navigate(`https://support.rubbl.com/owners`);
            },
          },
        ],
      });

      this.menuItems.push(
        connectLandingPages,
        {
          label: "List a machine",
          route: "/lp/list-your-machines",
          borderBottom: true,
        },
        {
          label: "(866) 455-8531",
          action: () => {
            AppNavigation.navigate(`tel:(866) 455-8531`);
          },
        },
        {
          label: "Sign up",
          action: () => this.authenticationService.signUp(),
        },
        {
          label: "Login",
          borderBottom: true,
          action: () => this.authenticationService.login(),
        },
      );
    }

  }

  toggleSection(section: MobileShopSection): void {
    this.openSection = this.openSection === section.key ? "" : section.key;
    if (this.openSection && section.route && !this.popularCategories[section.key]) this.loadPopularCategories(section);
  }

  categoriesFor(handle: string): MobileCategory[] {
    return this.machineCategories[handle] || [];
  }

  private loadAttachmentMenus(): void {
    if (!this.storefrontEnabled || !this.attachmentMachineTypes.length) return;
    forkJoin(this.attachmentMachineTypes.map(machine =>
      this.http.get<MobileCategory[]>(this.categoriesEndpoint, { params: new HttpParams().set("machineType", machine.handle) }).pipe(
        map(categories => ({ handle: machine.handle, categories: (categories || []).filter(category => category.count > 0 && !!category.handle) })),
        catchError(() => of({ handle: machine.handle, categories: [] as MobileCategory[] })),
      ),
    )).subscribe(results => {
      for (const result of results) this.machineCategories[result.handle] = result.categories;
    });
  }

  toggleMachine(handle: string): void {
    this.openMachine = this.openMachine === handle ? "" : handle;
    if (!this.openMachine || this.machineCategories[handle]) return;
    this.machineCategories[handle] = [];
    this.http.get<MobileCategory[]>(this.categoriesEndpoint, { params: new HttpParams().set("machineType", handle) }).subscribe({
      next: categories => (this.machineCategories[handle] = categories.filter(category => category.count > 0 && !!category.handle)),
      error: () => (this.machineCategories[handle] = []),
    });
  }

  toggleState(stateAbbr: string): void {
    this.openState = this.openState === stateAbbr ? "" : stateAbbr;
  }

  locationRoute(city: string, stateAbbr: string): string {
    return headerLocationRoute(city, stateAbbr);
  }

  private loadPopularCategories(section: MobileShopSection): void {
    const request = new AzureSearchRequest();
    request.search = "*";
    request.top = 0;
    const base = section.key === "buy" ? MARKETPLACE_BASE_FILTER.replace("(rentalRate ne 0) and ", "") : MARKETPLACE_BASE_FILTER;
    request.filter = base + (section.filter || "");
    request.searchFields = "primaryType, indexKey";
    request.facets = ["primaryType,count:50"];
    this.popularCategories[section.key] = [];
    this.azureSearchService.search<InventoryItem>(request, "machines").subscribe({
      next: result => {
        const facets = (result.search.facets.primaryType || []) as { value: string; count: number }[];
        this.popularCategories[section.key] = facets
          .filter(facet => facet.value !== ATTACHMENTS_PRIMARY_TYPE)
          .sort((a, b) => b.count - a.count)
          .slice(0, 26)
          .map(facet => ({ name: facet.value, count: facet.count }));
      },
      error: () => (this.popularCategories[section.key] = []),
    });
  }

  ngOnDestroy() {
    this.active = false;
    this.overlaySubscription?.unsubscribe();
    this.authenticationSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }
}
